import React, { useState, useEffect, useCallback, useRef } from "react";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../../store/api";
import _ from "lodash";
import mimsLogo from "../../../assets/images/MIMS-logo.png";

type MedicationAutocompleteProps = {
  id: any;
  value: string | null;
  isError: boolean;
  helperText: string;
  passSelection: (v: string, brandId: string | null,  monographId: string | null, monographType: string | null) => void;
  InputProps:any|null;
  SetMedname:any;
};

type optionValue = {
  brandId: string;
  brandName: string;
};

const MedicationAutocomplete = (props: MedicationAutocompleteProps) => {
  const { id, value, isError, helperText, passSelection,InputProps } = props;
  const classes = useStyles();
  const [optionsList, setOptionsList] = useState<optionValue[]>([]);
  const [inputValue, setInputValue] = useState(value || "");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [noScrolling, setNoScrolling] = useState(false);
  useEffect(() => {
    setInputValue(value || "")
  }, [value])
  const getSuggestionsApi = async () => {
    if (inputValue.length < 3) {
      setOptionsList([]);
      return;
    }
    setIsLoading(true);
    try {
      const res = await api.searchForBrands(`searchString=${inputValue}&page=${page}`);
      const newList = res.data?.model || [];
      if (newList.length === 0) {
        setNoScrolling(true);
      } else {
        if (page === 1) {
          setOptionsList(newList);
        } else {
          setOptionsList([...optionsList, ...newList]);
        }
      }
    } catch {}
    setIsLoading(false);
  };

  const debouncedApiCall = useCallback(_.debounce(getSuggestionsApi, 500), [inputValue, page]);

  const throttledPageIncease = _.throttle(
    () => {
      setPage(page + 1);
    },
    500,
    { leading: false }
  );

  const getDetailedInfoId = async (str: string) => {
    //   
    const brand = _.find(optionsList, { brandName: str });
    if (brand?.brandId && brand?.brandId !== "") {
      try {
        const res = await api.getBrandMonograph(`brandId=${brand.brandId}`);
        const monographId = res.data?.model?.monographId;
        const monographType = res.data?.model?.monographType;      
        passSelection(str, brand.brandId, monographId, monographType);
        props.SetMedname(brand.brandId);        
      } catch {}
    }
  };

  useEffect(() => {
    debouncedApiCall();
    passSelection(inputValue, null, null, null);
    return debouncedApiCall.cancel;
  }, [inputValue, page, debouncedApiCall]);

  return (
    <Autocomplete
      id={id || "auto-complete"}
      freeSolo
      disableClearable={true}
      blurOnSelect={true}
      options={optionsList.map((o) => o.brandName)}
      classes={{
        inputRoot: classes.autoComplete,
        listbox: classes.listbox,
        paper: optionsList.length > 0 ? classes.paper : classes.paperNoLogo,
        option: classes.option,
        inputFocused: classes.inputFocused,
      }}
      ListboxProps={{
        onScroll: (event: React.SyntheticEvent) => {
          const listboxNode = event.currentTarget;
          if (listboxNode.scrollTop + listboxNode.clientHeight + 5 >= listboxNode.scrollHeight) {
            if (!noScrolling) {
              throttledPageIncease();
            }
          }
        },
      }}
      filterOptions={(options: any, state: object) => options}
      // open={true}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(e: any, value: string, reason: string) => {
        setPage(1);
        setNoScrolling(false);
        setInputValue(value);                   
        if (reason === "reset") {
          getDetailedInfoId(value);
        }
      }}
      onChange={(event: any, value: string, reason: string) => {
        if (reason === "select-option") {
          getDetailedInfoId(value);
        }
      }}
      renderInput={(params) => (     
        <TextField
          {...params}
          variant="outlined"
          error={isError}
          helperText={helperText}               
          InputProps={{
            ...params.InputProps,
            ...InputProps
          }} 
        />       
      )}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  autoComplete: {
    marginTop: 3,
    padding: "0 0 0 9px !important",
  },
  listbox: {
    padding: 0,
  },
  inputFocused: {
    padding: 0,
  },
  paperNoLogo: {
    margin: 0,
    boxShadow: "none",
    borderTopLeftRadius: "0",
    borderTopRightRadius: "0",
  },
  paper: {
    margin: 0,
    boxShadow: "none",
    borderTopLeftRadius: "0",
    borderTopRightRadius: "0",

    "&::after": {
      content: `url(${mimsLogo})`,
      display: "block",
      padding: "18px 0 14px",
      margin: "auto",
      backgroundColor: "#F1F1F1",
      textAlign: "center",
      boxShadow: "0px -8px 23px -5px rgba(0,0,0,0.15)",
    },
  },
  option: {
    fontWeight: "bold",
    fontSize: "15.6px",
    lineHeight: "19px",
    color: "#02489D",
    borderTop: "1px solid rgba(0, 0, 0, 0.07)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: "#F1F1F1",
    display: "block",
  },
}));

export default MedicationAutocomplete;
